import { ChevronDownIcon, PlusIcon, SearchIcon } from "lucide-react";
import { cn } from "~/lib/utils";
import {
  PLANNING_ICON_CONTROL,
  PLANNING_ICON_CONTROL_IDLE,
  PLANNING_ICON_CONTROL_ACTIVE,
  PLANNING_ICON_CONTROL_PRIMARY,
} from "./planningIconControl";

export interface PlanningViewOption<View extends string> {
  readonly value: View;
  readonly label: string;
  readonly icon: React.ComponentType<{ className?: string }>;
}

export interface PlanningViewSwitcherProps<View extends string> {
  readonly value: View;
  readonly options: ReadonlyArray<PlanningViewOption<View>>;
  readonly onChange: (value: View) => void;
  readonly showLabels?: boolean;
  /** `segmented` is the planning surface default; `icons` matches Linear circular controls. */
  readonly variant?: "segmented" | "icons";
  /** Stretch segmented options evenly across the available width. */
  readonly fill?: boolean;
  readonly className?: string;
}

export interface PlanningSearchInputProps {
  readonly value: string;
  readonly onChange: (value: string) => void;
  readonly placeholder: string;
  readonly ariaLabel: string;
  readonly className?: string;
  /** `pill` uses a quieter rounded search for Linear project chrome. */
  readonly shape?: "default" | "pill";
}

export interface PlanningNewButtonProps {
  readonly label: string;
  readonly onClick: () => void;
  readonly ariaLabel?: string;
  readonly hasMenu?: boolean;
  readonly disabled?: boolean;
  /** `icon` is a circular + control for Linear trailing clusters. */
  readonly shape?: "default" | "icon";
}

export function PlanningViewSwitcher<View extends string>({
  value,
  options,
  onChange,
  showLabels = false,
  variant = "segmented",
  fill = false,
  className,
}: PlanningViewSwitcherProps<View>) {
  if (variant === "icons") {
    return (
      <div
        className={cn("flex shrink-0 items-center gap-1.5", className)}
        role="group"
        aria-label="Task layout"
      >
        {options.map(({ value: optionValue, label, icon: Icon }) => {
          const pressed = value === optionValue;
          return (
            <button
              key={optionValue}
              type="button"
              onClick={() => onChange(optionValue)}
              aria-label={label}
              title={label}
              aria-pressed={pressed}
              className={cn(
                PLANNING_ICON_CONTROL,
                pressed ? PLANNING_ICON_CONTROL_ACTIVE : PLANNING_ICON_CONTROL_IDLE,
              )}
            >
              <Icon className="size-3.5 shrink-0" aria-hidden />
            </button>
          );
        })}
      </div>
    );
  }

  return (
    <div
      className={cn(
        "flex items-center gap-0.5 rounded-md bg-muted/50 p-0.5",
        fill ? "w-full" : "shrink-0",
        className,
      )}
      role="group"
      aria-label="Task layout"
    >
      {options.map(({ value: optionValue, label, icon: Icon }) => (
        <button
          key={optionValue}
          type="button"
          onClick={() => onChange(optionValue)}
          aria-label={label}
          title={label}
          aria-pressed={value === optionValue}
          className={cn(
            "flex items-center justify-center gap-1.5 rounded-[5px] transition-colors",
            fill ? "h-7 min-w-0 flex-1 px-2" : "size-touch md:size-auto md:h-7 md:px-2",
            !fill && showLabels ? "md:w-auto md:min-w-0" : null,
            !fill && !showLabels ? "md:w-7" : null,
            value === optionValue
              ? "bg-background text-foreground shadow-xs"
              : "text-muted-foreground hover:text-foreground",
          )}
        >
          <Icon className="size-3.5 shrink-0" />
          {showLabels ? <span className="truncate text-xs font-medium">{label}</span> : null}
        </button>
      ))}
    </div>
  );
}

export function PlanningSearchInput({
  value,
  onChange,
  placeholder,
  ariaLabel,
  className,
  shape = "default",
}: PlanningSearchInputProps) {
  const pill = shape === "pill";
  return (
    // Below md the search takes the leftover room. It used to be the only
    // shrinkable control next to a flex-1 spacer, so at 390px it collapsed to
    // ~40px and you could not read your own query.
    <label
      className={cn(
        "relative w-full min-w-touch md:w-56 md:flex-none",
        pill ? "md:w-44 lg:w-52" : null,
        className,
      )}
    >
      <SearchIcon
        className={cn(
          "pointer-events-none absolute top-1/2 size-3.5 -translate-y-1/2 text-muted-foreground/60",
          pill ? "left-2.5" : "left-2.5",
        )}
      />
      <input
        type="search"
        value={value}
        onChange={(e) => onChange(e.currentTarget.value)}
        placeholder={placeholder}
        aria-label={ariaLabel}
        className={cn(
          "w-full min-w-0 border text-no-zoom",
          "text-foreground placeholder:text-muted-foreground/50",
          "transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
          pill
            ? "h-7 rounded-full border-border/70 bg-muted/40 pl-8 pr-3 text-xs hover:border-border hover:bg-muted/60"
            : "min-h-touch rounded-md border-border bg-transparent pl-8 pr-2.5 md:h-8 md:text-xs hover:border-border",
        )}
      />
    </label>
  );
}

export function PlanningNewButton({
  label,
  onClick,
  ariaLabel,
  hasMenu = false,
  disabled = false,
  shape = "default",
}: PlanningNewButtonProps) {
  if (shape === "icon") {
    return (
      <button
        type="button"
        onClick={onClick}
        disabled={disabled}
        aria-label={ariaLabel ?? `New ${label}`}
        title={ariaLabel ?? `New ${label}`}
        className={cn(PLANNING_ICON_CONTROL, PLANNING_ICON_CONTROL_PRIMARY)}
      >
        <PlusIcon className="size-3.5" aria-hidden />
      </button>
    );
  }

  return (
    <button
      type="button"
      onClick={onClick}
      disabled={disabled}
      aria-label={ariaLabel}
      className={cn(
        "inline-flex min-h-touch min-w-touch items-center justify-center gap-1.5 whitespace-nowrap rounded-md border px-3 text-xs font-medium transition-colors md:h-8 md:min-h-0 md:min-w-0",
        "border-border bg-foreground text-background",
        "hover:opacity-90",
        "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring",
      )}
    >
      <PlusIcon className="size-3.5" />
      <span className="hidden md:inline">{label}</span>
      {hasMenu ? <ChevronDownIcon className="size-3 opacity-70" /> : null}
    </button>
  );
}
